'use client';

import type { FC } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { DEFAULT_SORTING, SORT_KEY, SORT_OPTIONS_UK } from '@core/Constants/Sort';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import type { DropdownOptionProps } from '@gds/Dropdown/Components/DropdownOption.types';
import { Dropdown } from '@gds/Dropdown/Dropdown';
import { Typography } from '@gds/Typography/Typography';
import { sortDrawerOpenAtom } from '@plp/Atoms/SortDrawerOpenAtom';
import { useUpdateFilters } from '@search-filters/Hooks/UseUpdateFilters';

import { trackFilterEvent } from '../../Filters.tracking';
import type { SortProps } from './Sort.entity';
import type { SortMessages } from './Sort.messages';
import SortDrawer from './SortDrawer';

import styles from './Sort.module.css';

const Sort: FC<SortProps> = ({ filterList, locale, sortMessages }) => {
  const sortFilter = getFilterFromArray(SORT_KEY, filterList);
  const filtersList = useAtomValue(filtersListAtom);
  const [sortDrawerIsOpen] = useAtom(sortDrawerOpenAtom);
  const { updateFilters } = useUpdateFilters();

  const sortOptions = SORT_OPTIONS_UK.map(option => ({
    label: sortMessages[option as keyof SortMessages],
    value: option,
  }));

  const handleSortChange = (option: DropdownOptionProps) => {
    trackFilterEvent(SORT_KEY, option.value as string, filtersList);
    updateFilters([{ id: SORT_KEY, value: option.value as string }]);
  };

  const selectedSortValue = sortFilter
    ? sortOptions.find(option => option.value === sortFilter?.value)
    : sortOptions.find(option => option.value === DEFAULT_SORTING[locale]);

  return (
    <>
      <div className={styles.wrapper} data-test-id="sort-draw">
        {DEFAULT_SORTING[locale] && (
          <Typography variant="subheading3">{sortMessages.sortBy}</Typography>
        )}
        <Dropdown
          dataTestId="sort-plp"
          options={sortOptions}
          placeholder={sortMessages.sortBy}
          value={selectedSortValue}
          uncontrolled
          onChange={option => {
            option && handleSortChange(option);
          }}
        />
      </div>
      {sortDrawerIsOpen && (
        <SortDrawer
          sortByText={sortMessages.sortBy}
          options={sortOptions}
          selectedValue={selectedSortValue!}
          selectOption={handleSortChange}
        />
      )}
    </>
  );
};

export default Sort;
